import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  currencyCaveat,
  eligibilityHeadline,
  FHB_DISCLAIMER,
  type FhbState,
  firstHomeSchemeRulesForDate,
  schemeLabel,
  verdictBullet,
} from "@aucash/rules/first-home-scheme";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// first-home-scheme — the government first-home-buyer SCHEME-ELIGIBILITY MAP,
// surfaced stateless-v1 (PLAN arch decision #1). One screen: a scenario form
// (state × price × income × deposit × build-type) -> a stateless
// `calc.firstHomeScheme.compute` query -> the result renders INLINE (no
// save-by-id, no auth gate, no persistence). Every word of the result comes
// from the rules-layer copy helpers so it can never drift from the API/SEO
// surfaces (info-not-advice: which schemes APPEAR available + the gates, never
// "you should buy"/"you qualify so apply"; figures come from @aucash/rules).
//
// REGULATORY (binding — the C2 NCCP kill-line): the schemes are clean
// government programs, but the downstream mortgage is NCCP-regulated credit and
// *credit assistance* is a licensed activity. This personalised-eligibility
// surface carries NO broker / lender / loan-product / pre-approval handoff —
// the rules-layer no-mortgage-handoff guard (`assertNoCreditHandoff`) holds at
// the UI too. The only outward links are the cited government scheme + state
// revenue office sources.

/** Today's assessment date — the contract/decision date drives the date-keyed
 *  ruleset (the QLD FHOG 30-Jun-2026 sunset). Resolved from the device clock at
 *  submit time, never hard-coded into the figures. */
function todayIso(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

type BuildType = "new" | "established";

const STATE_OPTIONS: ReadonlyArray<{ value: FhbState; label: string }> = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
];

const BUILD_OPTIONS: ReadonlyArray<{ value: BuildType; label: string }> = [
  { value: "new", label: "New build" },
  { value: "established", label: "Established" },
];

const DEPOSIT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "0.02", label: "2%" },
  { value: "0.05", label: "5%" },
  { value: "0.1", label: "10%" },
  { value: "0.2", label: "20%+" },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.firstHomeScheme.compute.query>
>;

export default function FirstHomeSchemeIndex(): JSX.Element {
  const router = useRouter();

  const [state, setState] = useState<FhbState>("NSW");
  const [price, setPrice] = useState("");
  const [income, setIncome] = useState("");
  const [depositFraction, setDepositFraction] = useState("0.05");
  const [buildType, setBuildType] = useState<BuildType>("new");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (price.trim() === "") next.price = "Enter the property price.";
    if (income.trim() === "")
      next.income = "Enter your (or your combined) annual income.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.firstHomeScheme.compute
      .query({
        input: {
          assessmentDate: todayIso(),
          scenario: {
            state,
            price: num(price),
            income: num(income),
            depositFraction: num(depositFraction),
            buildType,
          },
        },
      })
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="First home schemes" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>First home buyer · NSW / VIC / QLD</Eyebrow>
            <Text style={styles.title}>Which first-home schemes apply to you?</Text>
            <Text style={styles.subtitle}>
              Check the four government first-home-buyer schemes at once — the
              Australian Government 5% Deposit Scheme, the First Home Super Saver,
              your state First Home Owner Grant and the first-home stamp-duty
              relief — and see which appear available on your figures and how they
              stack. General information about government schemes only.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which state is the property in?">
            <Segmented<FhbState>
              options={STATE_OPTIONS}
              value={state}
              onChange={(v) => {
                setState(v);
                setResult(null);
              }}
              testIDPrefix="state"
            />
          </Field>

          <Field
            label="Property price"
            help="The purchase price — sets the price-cap, grant value-cap and stamp-duty threshold tests."
            error={errors.price}
          >
            <MoneyInput
              value={price}
              onChange={setPrice}
              testID="input-price"
              accessibilityLabel="Property price"
            />
          </Field>

          <Field
            label="Your annual income"
            help="Your (or combined, if buying as a couple) annual income. The 5% Deposit Scheme dropped its income cap on 1 October 2025; income still matters for historical periods."
            error={errors.income}
          >
            <MoneyInput
              value={income}
              onChange={setIncome}
              testID="input-income"
              accessibilityLabel="Your annual income"
            />
          </Field>

          <Field
            label="Your deposit"
            help="As a share of the price. The 5% Deposit Scheme needs at least 5% (2% for the single-parent Family Home Guarantee stream)."
          >
            <Segmented<string>
              options={DEPOSIT_OPTIONS}
              value={depositFraction}
              onChange={(v) => {
                setDepositFraction(v);
                setResult(null);
              }}
              testIDPrefix="deposit"
            />
          </Field>

          <Field
            label="Is it a new build or an established home?"
            help="The First Home Owner Grant is new-build only in NSW, VIC and QLD; QLD new builds also get full stamp-duty exemption regardless of value."
          >
            <Segmented<BuildType>
              options={BUILD_OPTIONS}
              value={buildType}
              onChange={(v) => {
                setBuildType(v);
                setResult(null);
              }}
              testIDPrefix="build"
            />
          </Field>

          <Button
            label={submitting ? "Checking…" : "Check the schemes →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="first-home-scheme-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? (
            <ResultBlock result={result} state={state} />
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer FHB_DISCLAIMER) sits at the TOP, before any
 * number / eligibility statement (validation §3.2 placement rule). NO credit /
 * broker / lender handoff appears anywhere on this personalised surface (C2). */
function ResultBlock({
  result,
  state,
}: {
  result: ComputeResult;
  state: FhbState;
}): JSX.Element {
  const { output } = result;
  // Resolve the same ruleset the API used (date-keyed) so the currency caveat
  // names the right period / verified date — figures stay from @aucash/rules.
  const rules = firstHomeSchemeRulesForDate(result.input.assessmentDate);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any eligibility statement. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FHB_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Scheme eligibility · {output.periodLabel}</Eyebrow>
        <Chip label="Information" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{eligibilityHeadline(output)}</Text>
        {output.eligibleSchemes.length > 0 ? (
          <View style={styles.chipRow}>
            {output.eligibleSchemes.map((s) => (
              <View key={s} style={styles.eligChip}>
                <Text style={styles.eligChipText}>{schemeLabel(s)}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </Card>

      <Section title="Scheme by scheme">
        {output.verdicts.map((v) => (
          <View key={v.scheme} style={styles.verdictRow}>
            <Text style={styles.bodyText}>{verdictBullet(v)}</Text>
          </View>
        ))}
      </Section>

      {output.stackingNotes.length > 0 ? (
        <Section title="How these schemes stack">
          {output.stackingNotes.map((note, i) => (
            <Text key={i} style={styles.bodyText}>
              • {note}
            </Text>
          ))}
        </Section>
      ) : null}

      <Section title="Currency & where these figures come from">
        <Text style={styles.bodyText}>{currencyCaveat(state, rules)}</Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.5,
    color: colors.ink,
  },
  chipRow: { flexDirection: "row", flexWrap: "wrap", gap: space.sm },
  eligChip: {
    backgroundColor: colors.honeyTint,
    borderRadius: radius.sm,
    paddingVertical: space.xs,
    paddingHorizontal: space.sm,
  },
  eligChipText: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12.5,
    color: colors.honeyDeep,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },
  verdictRow: {},

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
